import { SigninEmailRO } from '@/apis/types/stepinAuth';
import { getCurrentOwnerData, putSigninEmailRO, type UserData } from '@/model/dexie/ownerDexie';
import { getTabSync } from '@/utilities/tabSync';
import Dexie, { type EntityTable } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';

export const currencies = [
	'USD',
	'KRW',
	'IDR',
	'PHP',
	'VND',
	'INR',
	'MYR',
	'RUB',
	'EUR',
	'BRL',
	'GBP',
	'MXN',
	'THB',
	'PLN',
	'SGD',
	'TRY',
	'CLP',
	'TWD',
	'CAD',
	'ARS',
	'PEN',
	'ZAR',
	'HKD',
	'COP',
	'AUD',
	'CZK',
	'RON',
	'SAR',
	'EGP',
	'UZS',
	'KZT',
	'SEK',
	'AED',
] as const;

export type Currency = (typeof currencies)[number];

export type TokenData = {
	index: 0;
	accessToken: string;
	refreshToken: string;
};

type CurrencyData = {
	index: 0;
	currency: Currency;
};

export const userDB = new Dexie('UserDB') as Dexie & {
	tokenData: EntityTable<TokenData, 'index'>;
	currencyData: EntityTable<CurrencyData, 'index'>;
};

userDB.version(5).stores({
	currencyData: '++index',
	tokenData: '++index',
});

// tokenDB.version(2).stores({
// 	tokenData: '++index,accessToken,refreshToken',
// })

/**	const tokens = useLiveQuery(async () => getToken()) */
export const getToken = () => {
	return userDB.tokenData.get(0);
};

/** TODO: 여기서 USER INFO 받음 */
export const saveUserDataToDexie = async (loginRO: SigninEmailRO) => {
	return putSigninEmailRO(loginRO);
};

export const saveTokenDataToDexie = async ({ accessToken, refreshToken }: TokenData) => {
	try {
		await userDB.tokenData.put({
			accessToken: accessToken,
			index: 0,
			refreshToken: refreshToken,
		});

		// 저장 검증: 실제로 저장되었는지 확인
		const savedToken = await userDB.tokenData.get(0);
		if (!savedToken || savedToken.accessToken !== accessToken) {
			throw new Error('토큰 저장 검증 실패');
		}

		console.log('[saveTokenDataToDexie] 토큰 저장 및 검증 완료:', {
			accessTokenPrefix: accessToken.substring(0, 20) + '...',
		});

		// 탭 동기화: 토큰 갱신 알림
		const tabSync = getTabSync();
		tabSync.broadcast('TOKEN_REFRESH', { accessToken, refreshToken });
	} catch (error) {
		console.error('[saveTokenDataToDexie] 토큰 저장 중 오류 발생:', error);
		throw error; // 에러를 다시 던져서 상위에서 처리하도록
	}
};

export const tokenUpdate = async (response: Response) => {
	const accessToken = response.headers.get('Accesstoken');
	const refreshToken = response.headers.get('Refreshtoken');
	if (!accessToken || !refreshToken) {
		console.warn('[tokenUpdate] 토큰이 response 헤더에 없습니다');
		return;
	}
	console.log('[tokenUpdate] 토큰 업데이트 시작:', {
		accessTokenPrefix: accessToken.substring(0, 20) + '...',
	});
	await saveTokenDataToDexie({ accessToken, index: 0, refreshToken });
	console.log('[tokenUpdate] 토큰 업데이트 완료');
	return { accessToken, refreshToken };
};

export const useGetUser = () => {
	return useLiveQuery(async () => {
		// const user = await userDB.userData.get(0)
		const user = await getCurrentOwnerData();
		const currency = await userDB.currencyData.get(0);
		if (user && currency) return { ...user, ...currency } as UserData & CurrencyData;
		return { ...user, currency: 'USD' };
	});
};

export const useGetToken = () => {
	return useLiveQuery(async () => {
		const tokens = await getToken();
		const users = await getCurrentOwnerData();

		if (tokens && users) return { ...tokens, users };

		console.log(tokens, users, '통화 필요');
	});
};

export const putCurrency = (currency: Currency) => {
	return userDB.currencyData.put({ currency, index: 0 });
};

export const getCurrecy = () => {
	return userDB.currencyData.get(0);
};

export const currencyGet = () => {
	return userDB.currencyData.get(0);
};

export const clearUserData = async () => {
	console.trace('clearUserData');
	await userDB.tokenData.clear();
	await userDB.currencyData.clear();

	// 탭 동기화: 로그아웃 알림
	const tabSync = getTabSync();
	tabSync.broadcast('LOGOUT');
};
